/**
 * components/riskTerminal/model.js
 *
 * Pure mapping from GET /upstox/panel (upstox-035/036) to what the ported
 * design renders. The design's renderVals() computed these from demo data;
 * here every value comes from the server, and anything the server could not
 * compute stays unknown (null → '—' plus the reason), never 0.
 */

import { C, inr0, hhmm, mmss } from './theme';

import { streamFault } from './liveStream';

export const TREND_STALE_S = 180;   // the server's conditions thresholds (conditions.py)
export const TREND_LOST_S = 600;
export const DATA_STALE_MS = 25000;  // the screen's own data older than this → stale (upstox-054: a heartbeat every 15 s)

// upstox-041: sign in to Upstox from the Upstox tab and land back on it
// (the server keeps the return target on the OAuth state row; fixed list only)
const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// upstox-053: in a NEW tab (return target `popup`), the terminal tab never leaves
export const UPSTOX_SIGNIN_URL = `${API}/upstox/login?return_to=popup`;

/** Grade object → the design's badge kind. */
export function gradeKind(grade) {
  if (!grade || !grade.computed) return 'ungraded';
  switch (grade.value) {
    case 'ALIGNED':
    case 'NO_MATERIAL_EXPOSURE': return 'aligned';          // no tag
    case 'AGAINST_FADING': return 'fading';
    case 'AGAINST_CONFIRMED': return 'confirmed';
    default: return 'ungraded';
  }
}

/**
 * "Not graded" as it reads (upstox-074, item 08) — only HOW it reads; the grading is unchanged.
 *   market closed (session_state closed): plain grey caps "NOT GRADED · MARKET CLOSED", no hatch, no border;
 *   in session: the hatched tag with the specific reason (grade_structure / _grade_payload), "grade stale"
 *     renamed "GRADING STOPPED <age>"; TREND TIED, HOLDING and MCX are plain grey caps.
 * Returns { label, plain, tip } — `tip` is the full reason for the hover card.
 */
export function notGradedTag(grade, status, nowMs) {
  const reason = grade && grade.reason ? String(grade.reason) : null;
  const closed = status && status.session_state === 'closed';
  if (closed) return { label: 'NOT GRADED · MARKET CLOSED', plain: true, tip: 'The market is closed: nothing is graded until the next session.' };
  const r = (reason || '').toLowerCase();
  if (r === 'trend tied') return { label: 'TREND TIED', plain: true, tip: 'The trend reading is exactly neutral: nothing to grade against.' };
  if (r === 'holding') return { label: 'HOLDING', plain: true, tip: 'A holding is outside the advisory layer and is never graded.' };
  if (r.startsWith('mcx')) return { label: 'MCX · NOT GRADED', plain: true, tip: reason };
  if (r === 'grade stale') {
    const at = grade && grade.graded_at ? Date.parse(grade.graded_at) : NaN;
    const age = Number.isFinite(at) && nowMs ? Math.max(0, Math.round((nowMs - at) / 60000)) + 'm' : '';
    return { label: 'NOT GRADED · GRADING STOPPED' + (age ? ' ' + age : ''), plain: false, tip: 'The last grade is more than 3 minutes old: the grading job has stopped writing.' };
  }
  if (r.startsWith('net delta not computed')) {
    return { label: r.includes('delta unavailable') ? 'NOT GRADED · LEG DELTA UNAVAILABLE' : 'NOT GRADED · NET DELTA NOT COMPUTED', plain: false, tip: reason };
  }
  return { label: ungradedLabel(grade), plain: false, tip: reason || '' };
}

/** The hatched tag's text: always states why (never "NO INTENT"). */
export function ungradedLabel(grade) {
  const why = grade && grade.reason ? String(grade.reason).toUpperCase() : 'NO GRADE YET';
  return 'NOT GRADED · ' + why;
}

export function gradeTitle(grade) {
  if (!grade) return '';
  const vs = 'vs ' + (grade.graded_against || 'NIFTY 50 · confirmed');
  if (grade.computed && grade.value === 'NO_MATERIAL_EXPOSURE') return 'neutral · ' + vs;
  if (grade.computed && grade.value === 'ALIGNED') return 'aligned · ' + vs;
  return vs + (grade.reason ? ' · ' + grade.reason : '');
}

/** NIFTY confirmed state → one stated trend. */
const STATE_WORDS = {
  bull_trend: 'bullish', bear_trend: 'bearish', bull_consolidation: 'bull consolidation',
  bear_consolidation: 'bear consolidation', neutral: 'neutral',
};

/**
 * The header's trend statement under the user's ACTIVE reading (upstox-044:
 * CONFIRMED or AGGRESSIVE), plus the other reading in a few words.
 */
export function trendOf(status) {
  const t = (status && status.trend) || {};
  const reading = t.reading || 'CONFIRMED';
  const aggressive = reading === 'AGGRESSIVE';
  const s = t.active_state !== undefined ? t.active_state : t.confirmed_state;
  const sinceObj = t.active_since !== undefined ? t.active_since : t.since;
  const since = sinceObj && sinceObj.age_seconds != null ? sinceObj : null;
  const ago = since ? agoText(since.age_seconds) + (since.floor ? '+' : '') : '';
  const vs = ' · every grade is vs NIFTY 50 · ' + reading.toLowerCase();
  const other = t.other_reading
    ? t.other_reading.toLowerCase() + ': ' + (STATE_WORDS[t.other_state] || 'unknown')
    : '';
  const base = { reading, aggressive, other };
  if (!s) return { ...base, known: false, label: 'TREND UNKNOWN', confirmed: false, sub: 'no ' + reading.toLowerCase() + ' NIFTY 50 state yet · nothing is graded' };
  const dir = s.startsWith('bull') ? 'BULLISH' : s.startsWith('bear') ? 'BEARISH' : 'NEUTRAL';
  if (dir === 'NEUTRAL') return { ...base, known: true, dir, label: 'NEUTRAL · NO TREND', confirmed: false, sub: 'NIFTY 50 · no trend' + (ago ? ' for ' + ago : '') + vs };
  if (aggressive) {
    return { ...base, known: true, dir, confirmed: true, label: dir + ' · AGGRESSIVE READING',
      sub: 'K1 vs E9' + (ago ? ' · ' + ago : '') + ' · against it is red: timers and the interrupt run' + vs };
  }
  const confirmed = s.endsWith('_trend');
  return {
    ...base, known: true, dir, confirmed,
    label: dir + (confirmed ? ' · CONFIRMED TREND' : ' · CONSOLIDATING'),
    sub: (confirmed ? 'confirmed ' + (ago ? ago + ' ago' : '') + ' · the system pushes back hard'
      : 'fading · the system pushes back softly') + vs,
  };
}

export function agoText(sec) {
  if (sec == null) return '';
  if (sec < 60) return Math.round(sec) + 's';
  if (sec < 3600) return Math.floor(sec / 60) + ' min';
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return h + 'h ' + m + 'm';
}

/** Feeds are silent when healthy; only faults occupy the bar. */
export function feedFaults(status, dataAgeMs, pollError, stream) {
  const out = [];
  // upstox-054: the live stream's own state (reconnecting, or down and polling)
  const sf = streamFault(stream);
  if (sf) out.push(sf);
  const f = (status && status.feed) || {};
  const live = status && status.session_state === 'live';
  // upstox-045: the market runs to 15:40 (session_state); the trend engine's
  // window ends at 15:14 — the trend's age is judged only inside it
  const ts = status && (status.trend_session_state !== undefined ? status.trend_session_state : status.session_state);
  const trendWatch = ts === 'live' || ts === 'stale';
  if (pollError || (dataAgeMs != null && dataAgeMs > DATA_STALE_MS)) {
    out.push({ name: 'SCREEN', v: (dataAgeMs != null ? agoText(dataAgeMs / 1000) + ' old · ' : '') + 'STALE' + (pollError ? ' · ' + pollError : ''), state: pollError ? 'lost' : 'stale' });
  }
  // upstox-041: no valid token → "UPSTOX NOT SIGNED IN · SIGN IN" (the screen makes it a link);
  // Upstox being down stays a plain fault with its reason
  // upstox-067: a Kite account names itself and says the adapter's own sentence
  const kiteAcct = !!(status && status.account && status.account.broker === 'KITE');
  if (!f.upstox_connected && status) {
    out.push(f.upstox_signin_needed && !kiteAcct
      ? { name: 'UPSTOX', v: 'NOT SIGNED IN', signin: true, state: 'lost' }
      : { name: kiteAcct ? 'KITE' : 'UPSTOX', v: (f.upstox_reason || 'not connected').toUpperCase(), state: 'lost' });
  }
  if (live && trendWatch) {
    const age = f.trend_age_seconds;
    if (age == null || age > TREND_LOST_S) out.push({ name: 'TREND', v: (age == null ? 'NO DATA' : agoText(age)) + ' · LOST, GRADING SUSPENDED', state: 'lost' });
    else if (age > TREND_STALE_S) out.push({ name: 'TREND', v: agoText(age) + ' · STALE', state: 'stale' });
  }
  if (live) {
    if (f.kite_live === false && !kiteAcct) out.push({ name: 'KITE', v: 'NO LIVE SESSION', state: 'lost' });
    // upstox-054: open rows' prices not from Kite — never an old price shown as live
    const pr = f.prices;
    if (pr && pr.label) out.push({ name: 'PRICES', v: pr.label.replace(/^prices /, '').toUpperCase(), state: 'stale', title: pr.label });
  }
  return out;
}

// ── the open-order row's closeness bar (upstox-069) ──
// Named so they can be tuned: the bar is empty at this distance (% of the fire price) or more,
// full at 0, and the fill and the LTP turn amber inside the second one.
export const CLOSE_EMPTY_PCT = 10;
export const CLOSE_AMBER_PCT = 1;

const STOP_TYPES = ['SL', 'SL-M'];

/**
 * The order side line (upstox-074, item A): "SIDE QTY @ PRICE" — no colour, no chip. Qty is units as the
 * broker reports; a part fill keeps filled/quantity. After the "@": the order's price when it has one,
 * else "SL-M" or "MKT" by type — never the trigger.
 */
export function sideLine(o) {
  const side = String(o.side || '').toUpperCase();
  const type = String(o.order_type || '').toUpperCase();
  const price = o.price > 0 ? Number(o.price).toFixed(2) : type === 'SL-M' ? 'SL-M' : type === 'MARKET' ? 'MKT' : '—';
  return `${side} ${qtyOfLine(o)} @ ${price}`;
}

function qtyOfLine(o) {
  const q = o.quantity, f = o.filled_quantity || 0;
  return f > 0 && q != null && f < q ? f + '/' + q : (q ?? '—');
}

/** An order's own price in words, for the small grey note beside a fill: "limit 984.00", "market", "SL-M"; null when it has none. */
export function limitWords(o) {
  const type = String(o.order_type || '').toUpperCase();
  if (o.price > 0) return 'limit ' + Number(o.price).toFixed(2);
  if (type === 'MARKET') return 'market';
  if (type === 'SL-M') return 'SL-M';
  return null;
}

/**
 * upstox-083 — how a COMPLETED order's side line reads: the AVERAGE FILL PRICE the order got ("SELL 1 @ 993.10";
 * a part fill keeps "BUY 25/50 @ <average>"), with the order's own limit / "market" as a separate small grey note.
 * `average_price` is the broker's own figure, stored by upstox-046 for Upstox and Kite alike (Upstox's order book
 * `average_price`; Kite's `average_price`), 0 / absent stored as null — a part fill carries the average so far.
 * An order with no fill (cancelled, rejected) keeps its order price. A fill whose average is not reported shows
 * the order price with the reason "fill price not reported" — never a guessed number.
 * Returns { text, limit, reason, filled }.
 */
export function fillLine(o) {
  const base = sideLine(o);
  if (!((o.filled_quantity || 0) > 0)) return { text: base, limit: null, reason: null, filled: false };
  const avg = Number(o.average_price);
  if (o.average_price != null && Number.isFinite(avg) && avg > 0) {
    return { text: `${String(o.side || '').toUpperCase()} ${qtyOfLine(o)} @ ${avg.toFixed(2)}`, limit: limitWords(o), reason: null, filled: true };
  }
  return { text: base, limit: null, reason: 'fill price not reported', filled: false };
}

/** What the row's four columns show: TYPE · TRIGGER · LIMIT · LTP — "—" where a type has none. */
export function orderTerms(o) {
  const type = String(o.order_type || '').toUpperCase();
  const hasTrigger = STOP_TYPES.includes(type);
  const hasLimit = type === 'LIMIT' || type === 'SL';
  return {
    type: type || '—',
    trigger: hasTrigger ? priceText(o.trigger_price) : '—',
    limit: hasLimit ? priceText(o.price) : '—',
    ltp: o.ltp == null ? '—' : priceText(o.ltp),
  };
}

/**
 * How close an open order is to firing. The fire price is the trigger (SL, SL-M) or the limit
 * price (LIMIT); d = |fire − LTP| ÷ fire × 100. fill = clamp(1 − d / CLOSE_EMPTY_PCT, 0, 1);
 * amber within CLOSE_AMBER_PCT. A stop is "triggered, not filled" when the LTP has reached or
 * passed the trigger on the side that fires it (a BUY stop: LTP ≥ trigger; a SELL stop: LTP ≤
 * trigger) — and also when the broker's own status says so (an SL order that has left
 * "trigger pending" for "open"); both are named in `by`. MARKET orders, and any order with no
 * live price, show no fill.
 */
export function closeness(o) {
  const type = String(o.order_type || '').toUpperCase();
  const fire = STOP_TYPES.includes(type) ? o.trigger_price : type === 'LIMIT' ? o.price : null;
  const ltp = o.ltp;
  const side = String(o.side || '').toUpperCase();
  const st = String(o.order_status || '').trim().toLowerCase();
  const brokerSaysTriggered = STOP_TYPES.includes(type) && (st === 'open' || st === 'open pending');
  const out = { fire: fire > 0 ? fire : null, d: null, fill: 0, amber: false, triggered: false, by: null };
  if (!(fire > 0) || !(ltp > 0)) {
    if (brokerSaysTriggered) { out.triggered = true; out.by = 'broker status'; }
    return out;                                         // MARKET, or no live price: an empty bar
  }
  out.d = Math.abs(fire - ltp) / fire * 100;
  out.fill = Math.min(1, Math.max(0, 1 - out.d / CLOSE_EMPTY_PCT));
  out.amber = out.d <= CLOSE_AMBER_PCT;
  const passed = STOP_TYPES.includes(type) && (side === 'BUY' ? ltp >= fire : side === 'SELL' ? ltp <= fire : false);
  if (passed || brokerSaysTriggered) {
    out.triggered = true; out.fill = 1; out.by = passed ? 'LTP past the trigger' : 'broker status';
  }
  return out;
}

// ── the status strip (upstox-074, items 01 and 04) ──

/** A trend state → its reading words (caps). null → NOT READY. */
export function readingWords(state) {
  switch (state) {
    case 'bull_trend': return 'BULLISH';
    case 'bear_trend': return 'BEARISH';
    case 'bull_consolidation': return 'BULLISH · CONSOLIDATING';
    case 'bear_consolidation': return 'BEARISH · CONSOLIDATING';
    case 'neutral': return 'NEUTRAL';
    default: return 'NOT READY';
  }
}

/**
 * The strip's trend: the selected method's direction headline, and two radio rows (Aggressive,
 * Confirmed) each with its own reading. The unselected row's reading is `differs` (amber) when it
 * is a different state from the selected one (direction, or trend vs consolidation); a method with
 * no reading is NOT READY and never amber. `status.trend` carries both readings every time the
 * trend engine has run (confirmed_state, aggressive_state); null only before it has a row/warm-up.
 */
export function trendStrip(status) {
  const t = (status && status.trend) || {};
  const sel = (t.reading || 'CONFIRMED').toUpperCase();
  const state = { AGGRESSIVE: t.aggressive_state ?? null, CONFIRMED: t.confirmed_state ?? null };
  const rows = ['AGGRESSIVE', 'CONFIRMED'].map(m => {
    const mine = state[m];
    const other = state[m === 'AGGRESSIVE' ? 'CONFIRMED' : 'AGGRESSIVE'];
    const selected = sel === m;
    return { method: m, name: m === 'AGGRESSIVE' ? 'Aggressive' : 'Confirmed', selected, state: mine, words: readingWords(mine),
      ready: mine != null, differs: !selected && mine != null && state[sel] != null && mine !== state[sel], other };
  });
  const s = state[sel];
  const dir = s == null ? 'NOT READY' : s.startsWith('bull') ? 'BULLISH' : s.startsWith('bear') ? 'BEARISH' : 'NEUTRAL';
  const solid = s != null && (sel === 'AGGRESSIVE' ? s !== 'neutral' : s.endsWith('_trend'));
  return { selected: sel, headline: dir, known: s != null, solid, rows };
}

/**
 * The strip's right end: ONE place for freshness. `faults` are feedFaults()'s items. Returns
 * { items: worst first [{ name, text, tone: 'red'|'amber', hover, signin }], grey: the quiet
 * session text when nothing is wrong }. A stale or unknown trend session is an amber TREND item
 * unless the TREND fault already says it.
 */
export function freshnessOf(status, faults) {
  const items = (faults || []).map(f => ({ name: f.name, text: String(f.v || '').toLowerCase(), tone: f.state === 'lost' ? 'red' : 'amber', hover: f.title || '', signin: !!f.signin }));
  const ss = status && status.session_state;
  const hasTrend = items.some(i => i.name === 'TREND');
  if (!hasTrend && ss === 'stale') {
    const age = status.feed && status.feed.trend_age_seconds;
    items.push({ name: 'TREND', text: 'stale' + (age != null ? ' ' + agoText(age) : ''), tone: 'amber', hover: 'the latest trend bar is older than 3 minutes' });
  } else if (!hasTrend && ss === 'unknown') {
    items.push({ name: 'TREND', text: 'no data today', tone: 'amber', hover: 'holiday or the fetcher has stopped' });
  }
  items.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'red' ? -1 : 1));
  const grey = items.length ? null : (ss === 'closed' ? 'MARKET CLOSED' : 'ALL FEEDS LIVE');
  return { items, grey };
}

/**
 * Why a group's P&L reads "—" (upstox-074, item 10): the rule is unchanged (no partial sums — "—" when any
 * leg's P&L is unknown); this words it. `pnl_reason` is figures.row_pnl's reason for that leg when the broker
 * row is unusable; otherwise the panel's own reason (not signed in, waiting for the first refresh) or "no
 * broker row for it". Returns null when every leg has a P&L.
 */
export function dashReason(legs, positionsReason) {
  const list = legs || [];
  const unknown = list.filter(l => l.pnl == null);
  if (!unknown.length) return null;
  const why = unknown.map(l => l.pnl_reason).find(Boolean) || positionsReason || 'no broker row for it';
  return `${unknown.length} of ${list.length} leg${list.length === 1 ? '' : 's'} ha${unknown.length === 1 ? 's' : 've'} no P&L · ${why}`;
}

/** A Kite MCX row is shown but never graded (upstox-067). */
export function mcxNote(instrumentKey) {
  return String(instrumentKey || '').startsWith('KITE:MCX:') ? 'MCX — not graded' : '';
}

export function sessionNote(status) {
  const s = status && status.session_state;
  if (!s || s === 'live') return 'ALL FEEDS LIVE';
  return 'SESSION ' + String(s).toUpperCase();
}

/** Money block values, from money (day_limit summary) and settings. */
export function moneyOf(money, settings) {
  const m = money || {};
  const inForce = (settings && settings.in_force) || {};
  const limit = m.daily_loss_limit ?? inForce.daily_loss_limit ?? null;
  const amber = m.amber ?? inForce.amber_loss_amount ?? null;
  const red = m.red ?? inForce.red_loss_amount ?? null;
  const zone = m.zone || null;
  const used = m.used_pct;
  const hue = zone === 'RED' || zone === 'OVER' ? C.red : zone === 'AMBER' ? C.amber : C.line4;
  let head = '', short = '';
  // upstox-068: no limit in force (a fresh Kite account) — say so; no figure, no bar, no percentage
  const noLimit = !!m.computed && limit == null && !zone;
  if (noLimit) { head = 'no day limit set'; short = 'no day limit set'; }
  else if (m.computed) {
    if (zone === 'UNTOUCHED') { head = 'UNTOUCHED · ' + inr0(limit) + ' LIMIT'; short = 'untouched'; }
    else if (zone === 'OVER') { head = 'OVER BY ' + inr0(m.over_by || 0); short = 'over by ' + inr0(m.over_by || 0); }
    else {
      head = inr0(m.left_amount) + ' LEFT · ' + (used ?? 0).toFixed(0) + '% USED';
      short = (used ?? 0).toFixed(0) + '% used · ' + inr0(m.left_amount) + ' left';
    }
  }
  return {
    computed: !!m.computed, reason: m.reason, total: m.total, realised: m.realised,
    unrealised: m.unrealised, holdings: m.holdings_total, limit, amber, red, zone, used, hue,
    over: zone === 'OVER', up: zone === 'UNTOUCHED', head, short, noLimit,
    amberPct: limit ? (amber / limit) * 100 : null, redPct: limit ? (red / limit) * 100 : null,
    events: m.zone_events_today || [],
  };
}

const KIND_LABEL = {
  INTERRUPT_DUE: 'INTERRUPT DUE', LIMIT_OVER: 'PAST DAY LIMIT', LIMIT_RED: 'DAY LIMIT RED',
  LIMIT_AMBER: 'DAY LIMIT AMBER', FEED_LOST: 'FEED LOST', FEED_STALE: 'FEED STALE',
  ORDER_REJECTED: 'ORDER REJECTED',
};

/** One server condition → the rail's lead/queue item (the server's ranking kept). */
export function conditionView(k, ctx) {
  const v = conditionViewBody(k, ctx);
  return { ...v, chip: chipText(v, k, ctx) };
}

function conditionViewBody(k, ctx) {
  const d = k.detail || {};
  const red = k.severity === 'RED';
  const base = { id: k.id, rank: k.rank, red, kindCode: k.kind, kind: KIND_LABEL[k.kind] || k.kind, actLabel: 'DETAIL', actDisabled: true, actTitle: 'Nothing to do here yet' };
  switch (k.kind) {
    case 'INTERRUPT_DUE': {
      const s = ctx.structuresById[d.structure_id];
      const off = s && s.offside;
      return { ...base, text: (d.structure_name || 'A structure') + ' has been held against a confirmed trend', timer: off ? mmss(ctx.elapsed(off)) : '', actLabel: 'ANSWER', actDisabled: false, actTitle: 'Open the interrupt', structureId: d.structure_id, episodeId: d.episode_id };
    }
    case 'EOD_OPEN_LEGS': {
      const legs = d.legs || [];
      return {
        ...base, kind: (ctx.eodTime || '') + " · TODAY'S LEGS STILL OPEN",
        text: legs.length + (legs.length === 1 ? ' leg' : ' legs') + ' booked as today’s trades ' + (legs.length === 1 ? 'is' : 'are') + ' still open.',
        legs: legs.map(l => [ctx.symbolOf(l.instrument_key), (l.structure_name || 'unclassified') + (l.intent ? ' · ' + l.intent : '')]),
        actLabel: "EXIT ALL TODAY'S LEGS", act2Label: 'REVIEW EACH', actTitle: 'This terminal places no orders yet — use the Upstox app',
      };
    }
    case 'LIMIT_OVER':
      return { ...base, text: 'Over by ' + inr0(d.over_by || 0) + ' · ' + (d.used_pct ?? 0).toFixed(0) + '% of ' + inr0(d.daily_loss_limit || 0), actLabel: 'REVIEW' };
    case 'LIMIT_RED':
    case 'LIMIT_AMBER':
      return { ...base, text: (d.used_pct ?? 0).toFixed(0) + '% used · ' + inr0(d.left_amount || 0) + ' left', actLabel: 'REVIEW' };
    case 'FEED_LOST':
      return { ...base, text: (d.causes || []).join(' · ') || 'feed lost', actLabel: 'DETAIL' };
    case 'FEED_STALE':
      return { ...base, text: 'TREND ' + agoText(d.trend_age_seconds) + ' old', actLabel: 'DETAIL' };
    case 'ORDER_REJECTED':
      // upstox-083: the instrument's name from the instrument master; the raw key only in the hover card (and as the
      // text itself only when the master has no row for it — an expired contract's row is gone)
      return { ...base, text: ctx.symbolOf(d.instrument_key) + ' · ' + (d.status_message || 'no reason given'), actLabel: 'DISMISS', actDisabled: false, actTitle: 'Dismiss this rejection',
        keyTip: d.instrument_key && ctx.symbolOf(d.instrument_key) !== d.instrument_key ? 'Instrument key ' + d.instrument_key : '' };
    default:
      return { ...base, text: '' };
  }
}

/**
 * upstox-045: the collapsed rail's chip — kind label + a short subject, e.g.
 * "15:00 · 2 LEGS OPEN", "ORDER REJECTED · NIFTY 22800 CE".
 */
export function chipText(v, k, ctx) {
  const d = (k && k.detail) || {};
  const up = x => String(x || '').toUpperCase();
  switch (v.kindCode) {
    case 'INTERRUPT_DUE': return 'INTERRUPT DUE · ' + up(d.structure_name || 'a structure') + (v.timer ? ' · ' + v.timer : '');
    case 'EOD_OPEN_LEGS': {
      const nl = (d.legs || []).length;
      return ((ctx && ctx.eodTime) || 'EOD') + ' · ' + nl + (nl === 1 ? ' LEG OPEN' : ' LEGS OPEN');
    }
    case 'LIMIT_OVER': return 'PAST DAY LIMIT · OVER BY ' + inr0(d.over_by || 0);
    case 'LIMIT_RED':
    case 'LIMIT_AMBER': return v.kind + ' · ' + (d.used_pct ?? 0).toFixed(0) + '% USED';
    case 'FEED_LOST': return 'FEED LOST · ' + up((d.causes || [])[0] || 'feed');
    case 'FEED_STALE': return 'FEED STALE · TREND ' + up(agoText(d.trend_age_seconds));
    case 'ORDER_REJECTED': {
      // the symbol without its expiry: "NIFTY 22800 CE 29 SEP 26" → "NIFTY 22800 CE"
      const sym = ctx && ctx.symbolOf ? String(ctx.symbolOf(d.instrument_key)) : String(d.instrument_key || '');
      const m = sym.match(/^(.*?\b(?:CE|PE|FUT))\b/);
      return 'ORDER REJECTED · ' + up(m ? m[1] : sym);
    }
    default: return v.kind;
  }
}

export function calmNote(conditions) {
  const c = conditions && conditions.last_cleared;
  if (!c) return '';
  return 'last condition cleared ' + hhmm(c.cleared_at) + ' · ' + (KIND_LABEL[c.kind] || c.kind).toLowerCase() + ', ' + (c.cleared_reason || '');
}

/** Signed quantity in units: +75 / −75; null → '—'. */
export function qtyText(q) {
  if (q === null || q === undefined) return '—';
  return (q < 0 ? '−' : q > 0 ? '+' : '') + Math.abs(q).toLocaleString('en-IN');
}

export function priceText(p) {
  if (p === null || p === undefined) return '—';
  return Number(p).toFixed(2);
}

/** An average price; unknown (null) is '—'. upstox-056: an open row's AVG is the
 * breakeven, which the server sends as null when unknown (never a placeholder 0) and
 * which can truly be ≤ 0 (earlier round trips already paid for the position), so any
 * finite number is shown. Closed rows' AVG BUY / AVG SELL come null for an unknown. */
export function avgText(p) {
  if (p === null || p === undefined || !Number.isFinite(Number(p))) return '—';
  return Number(p).toFixed(2);
}

/** Sum of known values; null if any is unknown (never a partial sum shown as whole). */
export function sumKnown(values) {
  let t = 0;
  for (const v of values) { if (v === null || v === undefined) return null; t += v; }
  return t;
}

// ── charges (upstox-046) — shown, never deducted; "≈" while an estimate ──

/** ₹ for a charge: paise under ₹100 (a single order's ₹25.50), whole rupees above. */
export function chargeAmt(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return '₹' + (Math.abs(n) < 100 ? n.toFixed(2) : Math.round(n).toLocaleString('en-IN'));
}

const SPLIT_ORDER = [['brokerage', 'brokerage'], ['stt', 'STT'], ['exchange', 'exchange'], ['gst', 'GST'], ['stamp', 'stamp'], ['sebi', 'SEBI']];

/** "brokerage ₹20.00 · STT ₹6.58 · exchange ₹1.56 · GST ₹3.88 · stamp ₹0.00 · SEBI ₹0.00" */
export function splitText(sp) {
  if (!sp) return '';
  return SPLIT_ORDER.map(([k, l]) => l + ' ' + chargeAmt(sp[k] || 0)).join(' · ');
}

function basisNote(basis) {
  if (basis === 'corrected') return 'BSE exchange charge and GST corrected from actuals';
  if (basis === 'api_low') return 'BSE: Upstox’s estimate, ≈ low — no posted day to correct it from yet';
  return '';
}

/** One order's amount: null (nothing filled) → nothing shown. */
export function orderCharges(ch) {
  if (!ch) return null;
  if (!ch.known) return { text: '≈₹?', title: 'Charges unknown — ' + (ch.reason || 'not estimated'), unknown: true };
  const note = basisNote(ch.basis);
  return {
    text: '≈' + chargeAmt(ch.total) + (ch.basis === 'api_low' ? ' low' : ''),
    title: 'Charges ≈ ' + chargeAmt(ch.total) + ' (' + (ch.label || 'Upstox’s estimate') + ')\n' + splitText(ch.split) + (note ? '\n' + note : ''),
    unknown: false,
  };
}

const EXPIRY_NOTE = 'to close assumes a trade; an option held to expiry in the money pays settlement STT at a different rate';

/** A position row's CHARGES cell: paid + ≈ to close; an unknown part reads "+ ?", never 0. */
/** upstox-074 (item 11): the four `unknown_part` wordings of charges.py, in the handoff's words. */
export function unknownWording(u) {
  const t = String(u || '');
  if (t.startsWith("orders before the terminal's record")) return 'opened before the terminal recorded orders';
  if (t.startsWith('no recorded order')) return 'no recorded order';
  if (/^order .* not estimated/.test(t)) return 'fill not priced yet';
  if (t.startsWith('to close')) return 'close cost not estimated';
  return t;
}

/** "NIFTY 23000 PE 29 DEC 26" → "23000 PE" (a leg's short name in a hover line). */
export function legShort(sym) {
  const m = String(sym || '').match(/^\S+ (\d+(?:\.\d+)?) (CE|PE)\b/);
  return m ? m[1] + ' ' + m[2] : String(sym || '');
}

/**
 * A position row's CHARGES cell: paid + ≈ to close; with an unknown part it reads "≥ ₹X" (at least the
 * known part) and the hover says "₹X known" and one line per leg. `legs` ([{ sym, c }]): the legs behind
 * the row (a group's, or the leg itself) — each leg's own unknown_part gives its line.
 */
export function rowCharges(c, legs) {
  const legLines = u => (legs || []).flatMap(l => ((l.c && l.c.unknown_part) || []).map(x => legShort(l.sym) + ': ' + unknownWording(x)))
    .concat(legs && legs.length ? [] : (u || []).map(unknownWording));
  if (!c) return { text: '—', title: '', unknown: true };
  const unknown = c.unknown_part && c.unknown_part.length;
  const known = (c.paid || 0) + (c.to_close || 0);
  if (c.paid == null && c.to_close == null) return { text: '—', title: ['nothing is known yet', ...legLines(c.unknown_part)].join('\n'), unknown: true, head: 'CHARGES' };
  if (unknown) {
    const lines = [chargeAmt(known) + ' known', ...legLines(c.unknown_part)];
    if (c.label) lines.push(c.label);
    return { text: '≥ ' + chargeAmt(known) + (c.low ? ' low' : ''), title: lines.join('\n'), unknown: true, head: 'CHARGES', dotted: true };
  }
  const text = '≈' + chargeAmt(known) + (c.low ? ' low' : '');
  const head = chargeAmt(c.paid || 0) + ' paid' + (c.to_close != null ? ' · ≈' + chargeAmt(c.to_close) + ' to close' : '');
  const lines = [head];
  if (c.label) lines.push(c.label);                       // upstox-067: Kite rows say whose calculation
  if (c.paid_split) lines.push('paid: ' + splitText(c.paid_split));
  if (c.to_close_split) lines.push('to close: ' + splitText(c.to_close_split));
  if (c.corrected) lines.push(basisNote('corrected'));
  if (c.low) lines.push(basisNote('api_low'));
  if (c.to_close != null) lines.push(EXPIRY_NOTE);
  return { text, title: lines.join('\n'), unknown: false, head: 'CHARGES', dotted: true };
}

/** CHARGES TODAY, and the last posted day for its hover. */
export function chargesToday(block) {
  const t = block && block.today;
  if (!t) return { text: '—', title: '' };
  const lp = block.last_posted;
  const posted = t.label ? t.label : lp ? 'Last posted day ' + lp.session_date + ': ' + (lp.estimate != null ? '≈' + inrPlain(lp.estimate) : '≈?') + ' → actual ' + inrPlain(lp.actual) : 'No posted day yet — Upstox posts the actual the next day';
  const text = t.total == null ? '≈₹?' : (t.unknown_orders ? chargeAmt(t.total) + ' + ?' : '≈' + chargeAmt(t.total)) + (t.low ? ' low' : '');
  const lines = ['Every order filled today, holdings included · ' + t.orders + ' filled' + (t.unknown_orders ? ' · ' + t.unknown_orders + ' not estimated yet' : ''), posted, 'Shown only — P&L and the day limit do not include charges'];
  return { text, title: lines.join('\n') };
}

function inrPlain(v) { return v == null ? '—' : Math.round(v).toLocaleString('en-IN'); }
